import { getFirestore, doc, getDoc, setDoc, updateDoc, deleteDoc, serverTimestamp, getDocs, collection, query, where, runTransaction } from 'firebase/firestore';
import { EmailType } from './emailService';

export type TemplateStatus = 'draft' | 'active' | 'inactive' | 'deprecated';

export const EMAIL_TEMPLATE_LOCALES = [
  { code: 'no', label: 'Norwegian' },
  { code: 'en', label: 'English' },
] as const;

export type EmailTemplateLocale = typeof EMAIL_TEMPLATE_LOCALES[number]['code'];

export interface EmailTemplate {
  id: string;
  type: string;
  locale: string;
  subjectTemplate: string;
  bodyTemplate: string;
  updatedAt: any;
  // New metadata fields
  status?: TemplateStatus;
  eventTags?: string[]; // eventShortName tags
  organizer?: string; // organizer name
  meta?: {
    sourceHashAtTranslation?: string;
    hashAlgo?: string;
    translatedFromId?: string;
    contentHash?: string;
    status?: string;
  };
}

/**
 * Fetches an email template from Firestore, creating a default stub if missing.
 */
export const getEmailTemplate = async (
  type: EmailType,
  locale: string = 'en'
): Promise<EmailTemplate> => {
  const db = getFirestore();
  const docId = `${type}_${locale}`;
  const ref = doc(db, 'emailTemplates', docId);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    return { id: snap.id, ...(snap.data() as Omit<EmailTemplate, 'id'>) };
  }
  // create stub
  const stub = {
    type,
    locale,
    subjectTemplate: '',
    bodyTemplate: '',
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, stub);
  return { id: docId, ...stub };
};

/**
 * Updates an existing email template in Firestore.
 */
export const updateEmailTemplate = async (
  type: string,
  locale: string,
  subjectTemplate: string,
  bodyTemplate: string,
  metadata?: Partial<Pick<EmailTemplate, 'status' | 'eventTags' | 'organizer'>>
): Promise<void> => {
  const db = getFirestore();
  const docId = `${type}_${locale}`;
  const ref = doc(db, 'emailTemplates', docId);
  const updateData: any = {
    subjectTemplate,
    bodyTemplate,
    updatedAt: serverTimestamp(),
  };
  
  if (metadata) {
    if (metadata.status !== undefined) updateData.status = metadata.status;
    if (metadata.eventTags !== undefined) updateData.eventTags = metadata.eventTags;
    if (metadata.organizer !== undefined) updateData.organizer = metadata.organizer;
  }
  
  await updateDoc(ref, updateData);
};

/**
 * Lists all email templates from Firestore.
 */
export const listEmailTemplates = async (): Promise<EmailTemplate[]> => {
  const db = getFirestore();
  const snaps = await getDocs(collection(db, 'emailTemplates'));
  return snaps.docs.map(d => ({ id: d.id, ...(d.data() as Omit<EmailTemplate, 'id'>) }));
};

/**
 * Gets all unique organizers from templates.
 */
export const getTemplateOrganizers = async (): Promise<string[]> => {
  const templates = await listEmailTemplates();
  const organizers = new Set<string>();
  
  templates.forEach(template => {
    if (template.organizer) {
      organizers.add(template.organizer);
    }
  });
  
  // Add default if no organizers found
  if (organizers.size === 0) {
    organizers.add('KrUltra');
  }
  
  return Array.from(organizers).sort();
};

/**
 * Gets all unique event tags from templates.
 */
export const getTemplateEventTags = async (): Promise<string[]> => {
  const templates = await listEmailTemplates();
  const eventTags = new Set<string>();
  
  templates.forEach(template => {
    if (template.eventTags) {
      template.eventTags.forEach(tag => eventTags.add(tag));
    }
  });
  
  return Array.from(eventTags).sort();
};

/**
 * Imports email templates into Firestore.
 */
export const importEmailTemplates = async (templates: EmailTemplate[]): Promise<void> => {
  const db = getFirestore();
  for (const tpl of templates) {
    const ref = doc(db, 'emailTemplates', tpl.id);
    try {
      console.log(`importEmailTemplates: writing template ${tpl.id}`, tpl);
      await setDoc(ref, {
        type: tpl.type,
        locale: tpl.locale,
        subjectTemplate: tpl.subjectTemplate,
        bodyTemplate: tpl.bodyTemplate,
        updatedAt: serverTimestamp(),
      });
      console.log(`importEmailTemplates: success for ${tpl.id}`);
    } catch (error) {
      console.error(`importEmailTemplates: failed for ${tpl.id}`, error);
      throw error;
    }
  }
};

/**
 * Adds a new email template to Firestore.
 */
export const addEmailTemplate = async (
  type: string,
  locale: string,
  subjectTemplate: string,
  bodyTemplate: string,
  metadata?: Partial<Pick<EmailTemplate, 'status' | 'eventTags' | 'organizer'>>
): Promise<void> => {
  const db = getFirestore();
  const docId = `${type}_${locale}`;
  const ref = doc(db, 'emailTemplates', docId);
  
  const templateData: any = {
    type,
    locale,
    subjectTemplate,
    bodyTemplate,
    updatedAt: serverTimestamp(),
  };
  
  if (metadata) {
    if (metadata.status !== undefined) templateData.status = metadata.status;
    if (metadata.eventTags !== undefined) templateData.eventTags = metadata.eventTags;
    if (metadata.organizer !== undefined) templateData.organizer = metadata.organizer;
  }
  
  await setDoc(ref, templateData);
};

/** Creates an empty template variant for every supported locale. */
export const addEmailTemplateSet = async (type: string): Promise<void> => {
  const normalizedType = type.trim();
  if (!normalizedType) throw new Error('Template type is required.');
  if (normalizedType.includes('/')) throw new Error('Template type cannot contain "/".');

  const db = getFirestore();
  const refs = EMAIL_TEMPLATE_LOCALES.map(({ code }) =>
    doc(db, 'emailTemplates', `${normalizedType}_${code}`)
  );
  await runTransaction(db, async transaction => {
    const existing = await Promise.all(refs.map(ref => transaction.get(ref)));
    if (existing.some(snapshot => snapshot.exists())) {
      throw new Error(`A template named "${normalizedType}" already exists for one or more locales.`);
    }

    EMAIL_TEMPLATE_LOCALES.forEach(({ code }, index) => {
      transaction.set(refs[index], {
        type: normalizedType,
        locale: code,
        subjectTemplate: '',
        bodyTemplate: '',
        status: 'draft',
        updatedAt: serverTimestamp(),
      });
    });
  });
};

/** Renames every locale variant of a template type in one atomic batch. */
export const renameEmailTemplateSet = async (oldType: string, newType: string): Promise<void> => {
  const normalizedType = newType.trim();
  if (!normalizedType) throw new Error('New template type is required.');
  if (normalizedType.includes('/')) throw new Error('Template type cannot contain "/".');
  if (normalizedType === oldType) return;

  const db = getFirestore();
  const sourceQuery = query(collection(db, 'emailTemplates'), where('type', '==', oldType));
  const sourceSnapshots = await getDocs(sourceQuery);
  if (sourceSnapshots.empty) throw new Error(`No templates found with type "${oldType}".`);

  const targets = sourceSnapshots.docs.map(source => ({
    source,
    ref: doc(db, 'emailTemplates', `${normalizedType}_${source.data().locale}`),
  }));
  const renamedIds = new Map(targets.map(({ source, ref }) => [source.id, ref.id]));

  await runTransaction(db, async transaction => {
    const [sources, conflicts] = await Promise.all([
      Promise.all(targets.map(({ source }) => transaction.get(source.ref))),
      Promise.all(targets.map(({ ref }) => transaction.get(ref))),
    ]);
    if (sources.some(snapshot => !snapshot.exists())) {
      throw new Error(`One or more locale variants of "${oldType}" no longer exist.`);
    }
    if (conflicts.some(snapshot => snapshot.exists())) {
      throw new Error(`A template named "${normalizedType}" already exists for one or more locales.`);
    }

    sources.forEach((source, index) => {
      const data = source.data();
      if (!data) throw new Error(`Locale variant "${source.id}" no longer exists.`);
      const translatedFromId = data.meta?.translatedFromId;
      transaction.set(targets[index].ref, {
        ...data,
        type: normalizedType,
        updatedAt: serverTimestamp(),
        ...(translatedFromId && renamedIds.has(translatedFromId) && {
          meta: {
            ...data.meta,
            translatedFromId: renamedIds.get(translatedFromId),
          },
        }),
      });
      transaction.delete(source.ref);
    });
  });
};
/**
 * Deletes an email template from Firestore.
 */

export const deleteEmailTemplate = async (
  id: string
): Promise<void> => {
  const db = getFirestore();
  const ref = doc(db, 'emailTemplates', id);
  await deleteDoc(ref);
};

// =============================================
// Email Boilerplate CRUD Operations
// =============================================

export interface EmailBoilerplate {
  id: string;
  tag: string;
  description: string;
  template: string;
  updatedAt: any;
}

/**
 * Lists all email boilerplates from Firestore.
 */
export const listEmailBoilerplates = async (): Promise<EmailBoilerplate[]> => {
  const db = getFirestore();
  const snaps = await getDocs(collection(db, 'emailBoilerplates'));
  return snaps.docs.map(d => ({ id: d.id, ...(d.data() as Omit<EmailBoilerplate, 'id'>) }));
};

/**
 * Adds a new email boilerplate to Firestore.
 */
export const addEmailBoilerplate = async (
  tag: string,
  description: string,
  template: string
): Promise<string> => {
  const db = getFirestore();
  // Use tag as document ID (sanitized)
  const docId = tag.toLowerCase().replace(/[^a-z0-9-]/g, '-');
  const ref = doc(db, 'emailBoilerplates', docId);
  await setDoc(ref, {
    tag,
    description,
    template,
    updatedAt: serverTimestamp(),
  });
  return docId;
};

/**
 * Updates an existing email boilerplate in Firestore.
 */
export const updateEmailBoilerplate = async (
  id: string,
  tag: string,
  description: string,
  template: string
): Promise<void> => {
  const db = getFirestore();
  const ref = doc(db, 'emailBoilerplates', id);
  await updateDoc(ref, {
    tag,
    description,
    template,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Deletes an email boilerplate from Firestore.
 */
export const deleteEmailBoilerplate = async (
  id: string
): Promise<void> => {
  const db = getFirestore();
  const ref = doc(db, 'emailBoilerplates', id);
  await deleteDoc(ref);
};
